import { ADMIN_PATH, PROCESS_VIEW_PATH } from "../constants/routes.js";

const GRAPH_SEARCH_STORAGE_KEY = "graphSearchParams";
const ADMIN_SECTION_REQUESTS = "requests";
const ADMIN_SECTION_TRACK_APPROVALS = "track-approvals";
const ADMIN_SECTION_TRACK_REQUESTS = "track-requests";
const ADMIN_SECTION_AUDIT_LOG = "audit-log";
const ADMIN_SECTION_PROCESS_FLOW = "process-flow";
const DEFAULT_ADMIN_SECTION = ADMIN_SECTION_REQUESTS;
const ENABLED_ADMIN_SECTIONS = Object.freeze([
  ADMIN_SECTION_REQUESTS,
  ADMIN_SECTION_TRACK_APPROVALS,
  ADMIN_SECTION_TRACK_REQUESTS,
  ADMIN_SECTION_AUDIT_LOG,
  ADMIN_SECTION_PROCESS_FLOW,
]);
const TRACK_STATUS_ACTION_LABELS = ["track status", "track approvals", ADMIN_SECTION_TRACK_APPROVALS, "go to track status"];
const REQUEST_APPROVAL_ACTION_LABELS = ["send for approval", "submit for approval", "request approval"];
const STATUS_APPROVED = "approved";
const STATUS_REJECTED = "rejected";
const STATUS_PENDING = "pending";
const QUERY_PARAM_STATUS = "status";
const QUERY_PARAM_VIEW = "view";
const TRACK_STATUS_RESULT_LABELS = Object.freeze({
  [STATUS_APPROVED]: "approved requests",
  [STATUS_REJECTED]: "rejected requests",
});
const TRACK_STATUS_FILTER_VALUES = Object.freeze([STATUS_APPROVED, STATUS_REJECTED, STATUS_PENDING]);
const TRACK_REQUEST_VIEW_VALUES = Object.freeze([STATUS_PENDING, STATUS_APPROVED, STATUS_REJECTED]);
const ADMIN_SCOPED_QUERY_PARAM_KEYS = Object.freeze([QUERY_PARAM_STATUS, QUERY_PARAM_VIEW]);
const EMPTY_QUERY_PARAMS = Object.freeze({});

const getSessionStorage = () => {
  if (typeof window === "undefined" || typeof window.sessionStorage === "undefined") {
    return null;
  }
  return window.sessionStorage;
};

export const getStoredGraphSearchParams = () => {
  const storage = getSessionStorage();
  if (!storage) {
    return "";
  }
  return storage.getItem(GRAPH_SEARCH_STORAGE_KEY) || "";
};

export const setStoredGraphSearchParams = (value) => {
  const storage = getSessionStorage();
  if (!storage) {
    return;
  }
  storage.setItem(GRAPH_SEARCH_STORAGE_KEY, value || "");
};

export const buildGraphPath = (searchParamsString = "") => {
  const normalized = typeof searchParamsString === "string" ? searchParamsString.trim() : "";
  const queryPart = normalized ? "?" + normalized : "";
  return PROCESS_VIEW_PATH + queryPart;
};

export const getStoredGraphPath = () => buildGraphPath(getStoredGraphSearchParams());

export const normalizeAdminSection = (section) => {
  if (!section) {
    return DEFAULT_ADMIN_SECTION;
  }
  return ENABLED_ADMIN_SECTIONS.includes(section) ? section : DEFAULT_ADMIN_SECTION;
};

export const isAdminSectionEnabled = (section) => ENABLED_ADMIN_SECTIONS.includes(section || "");

export const normalizeTrackStatusFilter = (status) => {
  const normalizedStatus = String(status || "")
    .trim()
    .toLowerCase();
  return TRACK_STATUS_FILTER_VALUES.includes(normalizedStatus) ? normalizedStatus : null;
};

export const normalizeTrackRequestsView = (view) => {
  const normalizedView = String(view || "")
    .trim()
    .toLowerCase();
  return TRACK_REQUEST_VIEW_VALUES.includes(normalizedView) ? normalizedView : null;
};

const sanitizeAdminQueryParams = (section, queryParams = {}) => {
  const sanitized = {};
  const safeQueryParams = queryParams && typeof queryParams === "object" ? queryParams : EMPTY_QUERY_PARAMS;

  if (section === ADMIN_SECTION_TRACK_APPROVALS) {
    const normalizedStatus = normalizeTrackStatusFilter(safeQueryParams[QUERY_PARAM_STATUS]);
    if (normalizedStatus) {
      sanitized[QUERY_PARAM_STATUS] = normalizedStatus;
    }
  }

  if (section === ADMIN_SECTION_TRACK_REQUESTS) {
    const normalizedView = normalizeTrackRequestsView(safeQueryParams[QUERY_PARAM_VIEW]);
    if (normalizedView) {
      sanitized[QUERY_PARAM_VIEW] = normalizedView;
    }
  }

  return sanitized;
};

export const normalizeAdminNavigationTarget = (target) => {
  if (!target || typeof target !== "object") {
    return null;
  }

  const normalizedSection = normalizeAdminSection(target.section);
  return {
    section: normalizedSection,
    queryParams: sanitizeAdminQueryParams(normalizedSection, target.queryParams),
  };
};

export const resolveAdminNavigationTarget = (messageOrActionLabel = "") => {
  if (messageOrActionLabel && typeof messageOrActionLabel === "object") {
    const explicitTarget = normalizeAdminNavigationTarget(messageOrActionLabel?.navigationTarget);
    if (explicitTarget) {
      return explicitTarget;
    }
  }

  const actionLabel = typeof messageOrActionLabel === "object" ? messageOrActionLabel?.actionLabel : messageOrActionLabel;
  const normalizedLabel = String(actionLabel || "")
    .trim()
    .toLowerCase();
  if (!normalizedLabel) {
    return null;
  }

  if (normalizedLabel.includes(TRACK_STATUS_RESULT_LABELS[STATUS_APPROVED])) {
    return {
      section: ADMIN_SECTION_TRACK_REQUESTS,
      queryParams: { [QUERY_PARAM_VIEW]: STATUS_APPROVED },
    };
  }

  if (normalizedLabel.includes(TRACK_STATUS_RESULT_LABELS[STATUS_REJECTED])) {
    return {
      section: ADMIN_SECTION_TRACK_REQUESTS,
      queryParams: { [QUERY_PARAM_VIEW]: STATUS_REJECTED },
    };
  }

  if (TRACK_STATUS_ACTION_LABELS.some((label) => normalizedLabel.includes(label))) {
    return {
      section: ADMIN_SECTION_TRACK_APPROVALS,
      queryParams: EMPTY_QUERY_PARAMS,
    };
  }

  if (REQUEST_APPROVAL_ACTION_LABELS.some((label) => normalizedLabel.includes(label))) {
    return {
      section: ADMIN_SECTION_REQUESTS,
      queryParams: EMPTY_QUERY_PARAMS,
    };
  }

  return null;
};

export const resolveAdminSectionFromActionLabel = (actionLabel = "") => {
  return resolveAdminNavigationTarget(actionLabel)?.section || null;
};

export const buildAdminPath = (section, baseSearch, queryOverrides) => {
  const params = new URLSearchParams(baseSearch || undefined);
  const normalizedSection = normalizeAdminSection(section || params.get("section"));
  const retainedScopedParams = {};

  ADMIN_SCOPED_QUERY_PARAM_KEYS.forEach((key) => {
    if (params.has(key)) {
      retainedScopedParams[key] = params.get(key);
    }
    params.delete(key);
  });

  if (queryOverrides && typeof queryOverrides === "object") {
    Object.entries(queryOverrides).forEach(([key, value]) => {
      if (value == null || value === "") {
        delete retainedScopedParams[key];
        return;
      }
      retainedScopedParams[key] = value;
    });
  }

  params.set("section", normalizedSection);
  Object.entries(sanitizeAdminQueryParams(normalizedSection, retainedScopedParams)).forEach(([key, value]) => {
    params.set(key, value);
  });

  const queryString = params.toString();
  const queryPart = queryString ? "?" + queryString : "";
  return ADMIN_PATH + queryPart;
};

export const getDefaultAdminPath = () => buildAdminPath(DEFAULT_ADMIN_SECTION);

export { GRAPH_SEARCH_STORAGE_KEY, DEFAULT_ADMIN_SECTION, ENABLED_ADMIN_SECTIONS };
